"use client";
import React, { useState } from "react";
import Image from "next/image";
import CommonTitle from "@/components/common/CommonTitle";
import CommonSubtitle from "@/components/common/CommonSubtitle";
import asana from "@/assets/images/connect-home/asana.svg";
import clickUp from "@/assets/images/connect-home/clickup.svg";
import smartsheet from "@/assets/images/connect-home/smartsheet.svg";
import wrike from "@/assets/images/connect-home/wrike.svg";
import jira from "@/assets/images/connect-home/jira.svg";
import monday from "@/assets/images/connect-home/monday.svg";
import trello from "@/assets/images/connect-home/trello.svg";
import linear from "@/assets/images/connect-home/linear.svg";
import gmail from "@/assets/images/connect-home/gmail.svg";
import microsoftOutlook from "@/assets/images/connect-home/microsoft-outlook.svg";
import googleCalendar from "@/assets/images/connect-home/google-calendar.svg";
import outlookCalendar from "@/assets/images/connect-home/outlook-calendar.svg";
import bg1Image from "@/assets/images/connect-home/bg-1.svg";
import bg2Image from "@/assets/images/connect-home/bg-2.svg";
import dropBoxImage from "@/assets/images/connect-home/drop-box.svg";
import { X, PlugIcon, PlusIcon, ArrowRightIcon } from "lucide-react";
import { MdDone } from "react-icons/md";
import brandColorLogo from "@/assets/images/connect-home/brand-colored.svg";
import Link from "next/link";
import krooloMockImage from "@/assets/images/connect-home/mock.svg";
import CustomButton from "@/components/common/CustomButton";
import notion from "@/assets/images/connect-home/notion.svg";

interface App {
  id: string;
  name: string;
  icon: any;
  url: string;
  features: string[];
}

const apps: App[] = [
  {
    id: "asana",
    name: "Asana",
    icon: asana,
    url: "/integrations/asana",
    features: [
      "Two-way task sync",
      "Project & task AI indexing",
      "Search across projects",
      "Chat with tasks & updates",
      "Auto status summaries",
    ],
  },
  {
    id: "clickup",
    name: "ClickUp",
    icon: clickUp,
    url: "/integrations/clickup",
    features: [
      "Sync tasks & docs",
      "AI-powered workspace indexing",
      "Unified search across spaces",
      "Generate task summaries",
    ],
  },
  {
    id: "smartsheet",
    name: "Smartsheet",
    icon: smartsheet,
    url: "/integrations/smartsheet",
    features: [
      "Sheet data synchronization",
      "AI insights from sheets",
      "Search rows & reports",
      "Automated workflow summaries",
    ],
  },
  {
    id: "wrike",
    name: "Wrike",
    icon: wrike,
    url: "/integrations/wrike",
    features: [
      "Real-time task sync",
      "Project AI indexing",
      "Cross-project search",
      "Smart activity summaries",
    ],
  },
  {
    id: "jira",
    name: "Jira",
    icon: jira,
    url: "/integrations/jira",
    features: [
      "Issue & sprint sync",
      "AI bug & ticket insights",
      "Search across boards",
      "Auto sprint summaries",
    ],
  },
  {
    id: "notion",
    name: "Notion",
    icon: notion,
    url: "/integrations/notion",
    features: [
      "Docs & database sync",
      "AI knowledge indexing",
      "Search pages instantly",
      "Chat with Notion workspace",
    ],
  },
  {
    id: "trello",
    name: "Trello",
    icon: trello,
    url: "/integrations/trello",
    features: [
      "Board & card sync",
      "AI card insights",
      "Search across boards",
      "Automated task summaries",
    ],
  },
  {
    id: "linear",
    name: "Linear",
    icon: linear,
    url: "/integrations/linear",
    features: [
      "Issue tracking sync",
      "AI-powered issue insights",
      "Search across teams",
      "Sprint & cycle summaries",
    ],
  },
  {
    id: "gmail",
    name: "Gmail",
    icon: gmail,
    url: "/integrations/gmail",
    features: [
      "Email sync & threading",
      "AI email summarization",
      "Search across inbox",
      "Smart reply suggestions",
    ],
  },
  {
    id: "outlook",
    name: "Outlook",
    icon: microsoftOutlook,
    url: "/integrations/outlook",
    features: [
      "Email & thread sync",
      "AI email insights",
      "Unified inbox search",
      "Auto email summaries",
    ],
  },
  {
    id: "google-calendar",
    name: "Google Calendar",
    icon: googleCalendar,
    url: "/integrations/google-calendar",
    features: [
      "Event & schedule sync",
      "AI meeting summaries",
      "Smart event search",
      "Auto agenda generation",
    ],
  },
  {
    id: "outlook-calendar",
    name: "Outlook Calendar",
    icon: outlookCalendar,
    url: "/integrations/outlook-calendar",
    features: [
      "Calendar sync",
      "Meeting AI summaries",
      "Search events quickly",
      "Smart scheduling insights",
    ],
  },
];

const ConnectEverything: React.FC<{
  title?: React.ReactNode;
}> = ({ title }) => {
  const [draggedApp, setDraggedApp] = useState<App | null>(null);
  const [connectedApp, setConnectedApp] = useState<App | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  const handleDragStart = (e: React.DragEvent, app: App) => {
    setDraggedApp(app);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", app.id);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const appId = e.dataTransfer.getData("text/plain");
    const app = apps.find((a) => a.id === appId);

    if (app) {
      setConnectedApp(app);
      setIsConnected(true);
    }
  };

  const handleDragEnd = () => {
    setDraggedApp(null);
  };

  return (
    <div className="bg-[#F5F5F5] py-12 xl:py-20">
      <div className="container mx-auto px-4">
        <div className="mb-8 text-center xl:mb-10">
          {title ? (
            <CommonTitle
              color="#202020"
              fontSizeClass="text-[24px] lg:text-[32px] mb-2 lg:mb-4 text-center"
              fontWeightClass="font-semibold"
              as="h2"
            >
              {title}
            </CommonTitle>
          ) : (
            <CommonTitle
              color="#202020"
              fontSizeClass="text-[24px] lg:text-[32px] mb-2 lg:mb-4 text-center"
              fontWeightClass="font-semibold"
              as="h2"
            >
              <span className="bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-transparent">
                Connects
              </span>{" "}
              with Everything.
            </CommonTitle>
          )}
          <CommonSubtitle fontSizeWeight="mt-3 text-center">
            Drag your favorite tool to see the magic
          </CommonSubtitle>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:gap-8 xl:grid-cols-3">
          <div className="col-span-1 rounded-2xl bg-white p-4 sm:col-span-2 xl:col-span-1">
            <div className="grid grid-cols-4 gap-4 sm:grid-cols-6 xl:grid-cols-4">
              {apps.map((app) => (
                <div
                  key={app.id}
                  className="flex flex-col items-center gap-2 rounded-lg p-3 transition-all"
                >
                  <div
                    draggable
                    onDragStart={(e) => handleDragStart(e, app)}
                    onDragEnd={handleDragEnd}
                    className="relative h-12 w-12 flex-shrink-0 cursor-grab p-2 active:cursor-grabbing lg:h-16 lg:w-16"
                  >
                    <Image
                      src={app.icon}
                      alt={app.name}
                      className="h-full w-full object-contain"
                      quality={100}
                    />
                  </div>
                  <p className="text-center text-xs font-medium text-[#202020]">
                    {app.name}
                  </p>
                </div>
              ))}
            </div>
          </div>
          <div
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className="relative col-span-1 flex min-h-[400px] flex-col items-center justify-center overflow-hidden rounded-2xl bg-[#6049E7] p-8"
            style={{
              backgroundImage: `url(${bg1Image.src})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }}
          >
            <div className="relative z-10 flex items-center justify-center">
              <div className="h-60 w-60">
                <Image
                  src={dropBoxImage}
                  alt="Dropbox - Connect with Kroolo"
                  className="h-full w-full object-contain"
                  quality={100}
                />
              </div>
              <div className="absolute right-[24px] top-[136px] z-10">
                <div className="relative flex h-20 w-20 items-center justify-center rounded-[15px] bg-white/80 p-2">
                  {connectedApp?.icon ? (
                    <Image
                      src={connectedApp?.icon}
                      alt={connectedApp?.name}
                      className="h-full w-full object-contain"
                      quality={100}
                    />
                  ) : (
                    <PlusIcon size={48} className="text-[#202020]" />
                  )}
                </div>
              </div>
              {connectedApp?.name && (
                <div className="absolute right-0 top-[104px] flex h-5 w-5 items-center justify-center rounded-full bg-white p-1">
                  <button
                    onClick={() => {
                      setConnectedApp(null);
                      setIsConnected(false);
                    }}
                  >
                    <X size={20} className="text-[#202020]" />
                  </button>
                </div>
              )}
            </div>
            {isConnected && (
              <div className="absolute bottom-6 left-1/2 z-10 mx-auto flex w-fit shrink-0 -translate-x-1/2 items-center gap-2 rounded-full bg-green-500 px-4 py-2">
                <MdDone size={16} className="text-white" />
                <span className="whitespace-nowrap text-[12px] font-medium text-white">
                  Connected Successfully
                </span>
              </div>
            )}
          </div>
          <div
            className="relative col-span-1 overflow-hidden rounded-2xl p-4 text-white xl:p-8"
            style={{
              backgroundImage: `url(${bg2Image.src})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }}
          >
            {connectedApp?.name ? (
              <div className="relative z-10 flex h-full flex-col justify-between">
                <div>
                  <div className="mb-4 flex items-center gap-3">
                    <div className="relative h-12 w-12 bg-white rounded-[8px] flex items-center justify-center p-2">
                      <Image
                        src={brandColorLogo}
                        alt="Kroolo logo"
                        className="h-full w-full object-contain"
                        quality={100}
                      />
                    </div>
                    <PlugIcon className="h-5 w-5 text-white" />
                    <div className="relative flex h-12 w-12 items-center justify-center rounded-[8px] bg-white/80 p-2">
                      <Image
                        src={connectedApp?.icon}
                        alt={connectedApp?.name}
                        className="h-full w-full object-contain"
                        quality={100}
                      />
                    </div>
                  </div>
                  <CommonTitle
                    color="#FFFFFF"
                    fontSizeClass="text-[20px] lg:text-[22px]"
                    fontWeightClass="font-semibold"
                    as="h3"
                  >
                    Kroolo + {connectedApp?.name}
                  </CommonTitle>
                  <CommonSubtitle
                    color="#FFFFFF"
                    fontSizeWeight="mb-4 text-sm leading-relaxed"
                  >
                    Index all docs, slides, and sheets. Chat with your knowledge
                    base.
                  </CommonSubtitle>
                  <ul className="space-y-3">
                    {connectedApp?.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <MdDone size={16} className="text-green-400" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-auto flex w-full items-center justify-end">
                  <Link
                    href={connectedApp?.url}
                    className="flex items-center gap-2 text-[16px] font-medium text-white/80 hover:text-white"
                  >
                    See More Details
                    <ArrowRightIcon size={20} className="-rotate-[45deg]" />
                  </Link>
                </div>
              </div>
            ) : (
              <div className="relative z-10 flex h-full flex-col justify-center gap-2">
                <div className="h-30 relative mx-auto w-full overflow-hidden">
                  <Image
                    src={krooloMockImage}
                    alt="Kroolo connected apps interface preview"
                    className="h-full w-full object-cover"
                    quality={100}
                    draggable={false}
                  />
                </div>
                <CommonTitle
                  as="h3"
                  color="#FFFFFF"
                  fontSizeClass="text-[20px] lg:text-[22px]"
                  fontWeightClass="font-semibold"
                >
                  Drag your favorite tool to see the magic
                </CommonTitle>
                <CommonSubtitle color="#FFFFFF" fontSizeWeight="mt-1">
                  Integrate with your favorite tools to get the most out of
                  Kroolo. Save hours of your time by eliminating app switching!
                </CommonSubtitle>
              </div>
            )}
          </div>
        </div>
        <div className="mt-8 flex justify-center">
          <CustomButton
            text="View All Integrations"
            url="/integrations"
            variant="primary"
            version="black"
          />
        </div>
      </div>
    </div>
  );
};

export default ConnectEverything;